import { useLayoutEffect } from "react";

function measurePanelHeight(content, filtersEl) {
  if (!content) return 0;

  const anchorTop = filtersEl
    ? filtersEl.getBoundingClientRect().top
    : content.getBoundingClientRect().top;

  return Math.ceil(content.getBoundingClientRect().bottom - anchorTop) + 1;
}

function measureNaturalFiltersHeight(filtersEl) {
  if (!filtersEl) return 0;
  return Math.ceil(
    Math.max(filtersEl.scrollHeight, filtersEl.getBoundingClientRect().height),
  );
}

function clearPanelHeight(root, filtersEl) {
  if (filtersEl) {
    filtersEl.style.removeProperty("height");
    filtersEl.style.removeProperty("max-height");
    filtersEl.style.removeProperty("min-height");
  }
  root.style.removeProperty("--sgc-filters-panel-height");
}

function applyPanelHeight(root, filtersEl, height) {
  if (height <= 0) return;

  root.style.setProperty("--sgc-filters-panel-height", `${height}px`);

  if (filtersEl) {
    filtersEl.style.setProperty("height", `${height}px`, "important");
    filtersEl.style.setProperty("max-height", `${height}px`, "important");
    filtersEl.style.setProperty("min-height", "0", "important");
  }
}

export function useSgcFiltersPanelHeight(
  contentRef,
  filtersRef,
  active = true,
  ...syncDeps
) {
  useLayoutEffect(() => {
    if (!active) return undefined;

    const content = contentRef?.current;
    if (!content) return undefined;

    const root = document.documentElement;
    let filtersEl = null;
    let rafId = 0;

    const apply = () => {
      filtersEl = filtersRef?.current ?? null;
      if (!filtersEl) return;

      // Liberar altura forzada para medir el tamaño natural del panel
      // (si no hay eventos, el contenido queda corto y aplastaría los filtros).
      clearPanelHeight(root, filtersEl);
      void filtersEl.offsetHeight;

      const contentHeight = measurePanelHeight(content, filtersEl);
      const naturalFiltersHeight = measureNaturalFiltersHeight(filtersEl);
      const height = Math.max(contentHeight, naturalFiltersHeight);
      if (height <= 0) return;
      applyPanelHeight(root, filtersEl, height);
    };

    const scheduleApply = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        apply();
      });
    };

    apply();
    const raf1 = window.requestAnimationFrame(apply);
    const raf2 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(apply);
    });

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleApply);
      observer.observe(content);

      const timelineMain = content.querySelector(".timeline-desktop-main");
      if (timelineMain) {
        observer.observe(timelineMain);
      }

      const timelineEl = content.querySelector(".timeline");
      if (timelineEl) {
        observer.observe(timelineEl);
        timelineEl.addEventListener("scroll", scheduleApply, { passive: true });
      }

      content.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", scheduleApply);
          img.addEventListener("error", scheduleApply);
        }
      });
    }

    window.addEventListener("resize", scheduleApply);

    const scrollParent = content.closest(".volcan-body-scroll");
    scrollParent?.addEventListener("scroll", scheduleApply, { passive: true });

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleApply);
      scrollParent?.removeEventListener("scroll", scheduleApply);
      const timelineEl = content.querySelector(".timeline");
      timelineEl?.removeEventListener("scroll", scheduleApply);
      content.querySelectorAll("img").forEach((img) => {
        img.removeEventListener("load", scheduleApply);
        img.removeEventListener("error", scheduleApply);
      });

      clearPanelHeight(root, filtersEl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef, filtersRef, active, ...syncDeps]);
}

const RESOURCE_CARD_SELECTOR = ".cartografia-card, .mapas-card";
const RESOURCE_FILTERS_HEIGHT_VAR = "--sgc-resource-filters-panel-height";

function measureCardFilterHeight(gridEl, filtersEl) {
  const firstCard = gridEl?.querySelector(RESOURCE_CARD_SELECTOR);
  if (!firstCard || !filtersEl) return 0;

  const filtersTop = filtersEl.getBoundingClientRect().top;
  const cardBottom = firstCard.getBoundingClientRect().bottom;

  return Math.max(0, Math.ceil(cardBottom - filtersTop) + 1);
}

function clearCardFilterHeight(root, filtersEl) {
  if (filtersEl) {
    filtersEl.style.removeProperty("height");
    filtersEl.style.removeProperty("max-height");
    filtersEl.style.removeProperty("min-height");
  }
  root.style.removeProperty(RESOURCE_FILTERS_HEIGHT_VAR);
}

function applyCardFilterHeight(root, filtersEl, height) {
  if (height <= 0) return;

  root.style.setProperty(RESOURCE_FILTERS_HEIGHT_VAR, `${height}px`);
  filtersEl.style.setProperty("height", `${height}px`, "important");
  filtersEl.style.setProperty("max-height", `${height}px`, "important");
  filtersEl.style.setProperty("min-height", "0", "important");
}

export function useSgcCardFiltersPanelHeight(
  gridRef,
  filtersRef,
  active = true,
  ...syncDeps
) {
  useLayoutEffect(() => {
    if (!active) return undefined;

    const grid = gridRef?.current;
    if (!grid) return undefined;

    const root = document.documentElement;
    let filtersEl = null;
    let rafId = 0;

    const apply = () => {
      filtersEl = filtersRef?.current ?? null;
      const currentGrid = gridRef?.current;
      if (!currentGrid || !filtersEl) return;

      // Quitar altura forzada (inline + variable CSS) para medir el tamaño natural
      // y evitar solapes al cambiar a vista lista (tarjetas más bajas).
      clearCardFilterHeight(root, filtersEl);
      void filtersEl.offsetHeight;

      const naturalFiltersHeight = measureNaturalFiltersHeight(filtersEl);
      const cardHeight = measureCardFilterHeight(currentGrid, filtersEl);
      const height = Math.max(cardHeight, naturalFiltersHeight);
      if (height <= 0) return;
      applyCardFilterHeight(root, filtersEl, height);
    };

    const scheduleApply = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        apply();
      });
    };

    apply();
    const raf1 = window.requestAnimationFrame(apply);
    const raf2 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(apply);
    });

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleApply);
      observer.observe(grid);

      const watchCard = () => {
        const firstCard = grid.querySelector(RESOURCE_CARD_SELECTOR);
        if (firstCard) observer.observe(firstCard);
      };
      watchCard();

      grid.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", scheduleApply);
          img.addEventListener("error", scheduleApply);
        }
      });
    }

    window.addEventListener("resize", scheduleApply);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleApply);
      grid.querySelectorAll("img").forEach((img) => {
        img.removeEventListener("load", scheduleApply);
        img.removeEventListener("error", scheduleApply);
      });

      clearCardFilterHeight(root, filtersEl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridRef, filtersRef, active, ...syncDeps]);
}
